import { createDefaultImageWriter, processImage } from '@messenger/pintura'
import type { ImageItem } from '@messenger/types'
import { createCutoutImageReader, withCorsQuery } from './cutoutImageReader'
import { getImageDimensions } from './getImageDimensions'

type SavedEdits = ImageItem['image']['edits'] | null | undefined
type Size = { width: number; height: number }

type ApplySavedEditsToCutoutArgs = {
  /** The accepted background-removal output, made from the unedited photo. */
  cutoutUrl: string
  /** The photo Pintura edited, whose pixels the saved edits are measured in. */
  photoUrl: string
  /** The item's saved Pintura edits. */
  edits: SavedEdits
  /** Stores the edited cutout and answers its URL, or null if that failed. */
  upload: (file: File) => Promise<string | null>
}

const isWholePhoto = (
  crop: NonNullable<ImageItem['image']['edits']['crop']>,
  photo: Size,
) =>
  crop.x === 0 &&
  crop.y === 0 &&
  crop.width === photo.width &&
  crop.height === photo.height

const hasShapes = (shapes: unknown[] | undefined) =>
  Array.isArray(shapes) && shapes.length > 0

/**
 * Whether the saved edits change any pixel of the photo they were saved on,
 * apart from a crop (which needs the photo's size to judge): a turn or a
 * mirror, a filter or adjustment, markup, a frame or a fill. Pintura saves
 * its untouched defaults with every export, so a default value is no edit.
 */
const changesPixelsBesidesCrop = (edits: NonNullable<SavedEdits>) =>
  Boolean(
    edits.rotation ||
      edits.flipX ||
      edits.flipY ||
      Object.values(edits.colorMatrix ?? {}).some(Boolean) ||
      (edits.gamma !== undefined && edits.gamma !== 1) ||
      edits.vignette ||
      edits.noise ||
      Object.values(edits.convolutionMatrix ?? {}).some(Boolean) ||
      hasShapes(edits.annotation) ||
      hasShapes(edits.decoration) ||
      hasShapes(edits.redaction) ||
      edits.frame ||
      (edits.backgroundColor && edits.backgroundColor[3] > 0),
  )

/**
 * The URL an accepted background removal should put on the editor item. The
 * cutout is made from the unedited photo, so every edit the item saved in
 * Pintura (its crop, rotation and flips, its filter and adjustments, its
 * markup and frame) is applied to the cutout through Pintura's own renderer,
 * exactly as Pintura draws them when it opens the cutout with those edits,
 * and the result is uploaded. The cutout is read at the photo's size
 * (`createCutoutImageReader`), where the edits were measured, and written as
 * a PNG, so its transparency is kept. With no edits that change a pixel the
 * cutout is used as it is. The stored cutout itself is never changed, so the
 * one cutout per photo stays reusable.
 */
export const applySavedEditsToCutout = async ({
  cutoutUrl,
  photoUrl,
  edits,
  upload,
}: ApplySavedEditsToCutoutArgs): Promise<string | null> => {
  if (!edits) return cutoutUrl
  const cropsPhoto =
    edits.crop !== undefined &&
    !isWholePhoto(edits.crop, await getImageDimensions(photoUrl))
  if (!cropsPhoto && !changesPixelsBesidesCrop(edits)) return cutoutUrl
  const { dest } = await processImage(withCorsQuery(cutoutUrl), {
    imageReader: createCutoutImageReader(photoUrl),
    imageWriter: createDefaultImageWriter({ mimeType: 'image/png' }),
    imageState: edits,
  })
  return upload(dest)
}
